import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

const Dashboard = ({ transactions, loading, error, apiBaseUrl }) => {
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  // Get current month statistics
  useEffect(() => {
    const fetchMonthlyStats = async () => {
      try {
        setStatsLoading(true);
        const response = await fetch(`${apiBaseUrl}/api/stats/${currentYear}/${currentMonth}`);
        const result = await response.json();
        
        if (result.success) {
          setMonthlyStats(result.data);
        }
      } catch (err) {
        console.error('統計データの取得に失敗しました:', err);
      } finally {
        setStatsLoading(false);
      }
    };

    if (apiBaseUrl) {
      fetchMonthlyStats();
    }
  }, [apiBaseUrl, currentYear, currentMonth]);

  // Get recent transactions (last 5)
  const recentTransactions = transactions.slice(0, 5);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        {error}
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="container">
        {/* Current Month Balance */}
        <div className="card balance-card">
          <div className="balance-label">
            {format(currentDate, 'yyyy年M月', { locale: ja })}の収支
          </div>
          <div className="balance-amount">
            {statsLoading ? '...' : formatCurrency((monthlyStats?.balance || 0))}
          </div>
          <div className="balance-label">
            {(monthlyStats?.balance || 0) >= 0 ? '黒字' : '赤字'}
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="stats-grid">
          <div className="stat-card income">
            <div className="stat-value">
              {statsLoading ? '...' : formatCurrency(monthlyStats?.income || 0)}
            </div>
            <div className="stat-label">今月の収入</div>
          </div>
          
          <div className="stat-card expense">
            <div className="stat-value">
              {statsLoading ? '...' : formatCurrency(monthlyStats?.expense || 0)}
            </div>
            <div className="stat-label">今月の支出</div>
          </div>
          
          <div className="stat-card engel">
            <div className="stat-value">
              {statsLoading ? '...' : `${monthlyStats?.engelCoefficient || 0}%`}
            </div>
            <div className="stat-label">エンゲル係数</div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="card">
          <h2>最近の取引</h2>
          {recentTransactions.length === 0 ? (
            <div className="empty-state">
              <p>取引履歴がありません</p>
            </div>
          ) : (
            <div className="transaction-list">
              {recentTransactions.map((transaction) => (
                <div key={transaction.id} className="transaction-item">
                  <div className="transaction-info">
                    <div className="transaction-category">
                      {transaction.category}
                    </div>
                    {transaction.memo && (
                      <div className="transaction-memo">
                        {transaction.memo}
                      </div>
                    )}
                    <div className="transaction-date">
                      {format(new Date(transaction.date), 'M月d日', { locale: ja })}
                    </div>
                  </div>
                  <div className={`transaction-amount ${transaction.type}`}>
                    {transaction.type === 'income' ? '+' : '-'}
                    {formatCurrency(transaction.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;